import React from 'react'
import { type InstructorDistribution } from '../../models/Distribution'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { Typography } from '@mui/material'
import PageHeader from '../../components/PageHeader'
import EmptyListCard from '../StudentSurveyListFeature/components/EmptyListCard'
import { AdminViewerSurveyListCard } from './AdminViewerDashboardCard'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import { grey } from '../../colors'

interface AdminViewerCardListFeatureProps {
  list: InstructorDistribution[]
  name: string
  isLoading: boolean
  controls?: React.ReactNode
  /**
   * A failed load must not render the empty-state card — that asserts the term has no surveys
   * when the request simply failed. Mirrors BC-7 on the instructor dashboard.
   */
  isError?: boolean
  onBackToFirstPage?: () => void
  /**
   * Paging context for the card's "survey N of M" aria-label. Without it a screen reader on page 2
   * announces the 21st card as the first. Optional so an unpaginated caller still renders right.
   */
  pageNumber?: number
  pageSize?: number
  totalResults?: number
}

export const AdminViewerCardListFeature: React.FC<AdminViewerCardListFeatureProps> = ({
  list,
  name,
  controls,
  isLoading,
  isError = false,
  onBackToFirstPage,
  pageNumber = 1,
  pageSize,
  totalResults
}): JSX.Element => {
  const cardsPerPage = pageSize ?? list?.length ?? 0
  const indexOffset = (pageNumber - 1) * cardsPerPage
  const announcedTotal = totalResults ?? list?.length

  return (
    <Box display='flex' flexDirection='column' maxWidth={720} width='100%' margin='auto'>

      <PageHeader
        title={name}
        pageTitleAriaLabel={`Survey results for${name}`}
        controls={controls}
        isCompactOnly
      />

      {/* The spinner lives here rather than replacing the whole page, because the term selector
          is passed in through `controls` above — swapping the page for a loader would take the
          term switcher away on every page change, which paging makes a frequent event. */}
      {isLoading
        ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400} mt={2}>
            <CircularProgress />
          </Box>
          )
        : isError
          ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={8} textAlign="center">
              <Typography fontSize="16px" fontWeight={500} color={grey[950]}>
                We couldn&apos;t load {pageNumber > 1 ? `page ${pageNumber} of these surveys` : 'these surveys'}.
              </Typography>
              {pageNumber > 1 && onBackToFirstPage != null && (
                <GenericButton
                  buttonType={ButtonType.Secondary}
                  buttonSize={ButtonSize.Medium}
                  label="Back to first page"
                  onClick={onBackToFirstPage}
                />
              )}
            </Box>
            )
          : (
            <>
              {list?.map((distribution, index) => (
                <AdminViewerSurveyListCard
                  // Keyed on the row's own primary key, not its array position. A row here is
                  // one SlotNode, and `slot_node_id` is what the backend orders and pages on
                  // (`end_timestamp, slot_node_id`), so it is the one field guaranteed unique
                  // per row. Index keys would match cards to positions instead of to surveys,
                  // which is wrong the moment paging changes what sits at position 0.
                  key={distribution.slotNodeId ?? `${distribution.slotId}:${distribution.divisionId}`}
                  surveyNumber={indexOffset + index + 1}
                  totalSurveys={announcedTotal}
                  distribution={distribution}
                />
              ))}
              {list.length === 0 && (
                // Past page 1, "no surveys available" asserts something false: the list shrank
                // under a remembered page number, so this page is empty while earlier ones are
                // not. The pager hides itself once `numPages` drops to 1, which would leave no way
                // back — hence the escape, matching the failed-page arm above.
                pageNumber > 1 && onBackToFirstPage != null
                  ? (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={8} textAlign="center">
                      <Typography fontSize="16px" fontWeight={500} color={grey[950]}>
                        There are no surveys on page {pageNumber} any more.
                      </Typography>
                      <GenericButton
                        buttonType={ButtonType.Secondary}
                        buttonSize={ButtonSize.Medium}
                        label="Back to first page"
                        onClick={onBackToFirstPage}
                      />
                    </Box>
                    )
                  : <EmptyListCard />
              )}
            </>
            )}
    </Box>
  )
}
